import React, { useState, useEffect, useRef } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import type { BlogPost } from '../lib/blog';
import { useNavigate, useLocation } from 'react-router-dom';

interface BlogCarouselProps {
  posts: BlogPost[];
  formatDate: (date: string) => string;
  translations: {
    buttons: {
      readMore: string;
    };
  };
}

export function BlogCarousel({ posts, formatDate, translations }: BlogCarouselProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const [visibleSlides, setVisibleSlides] = useState(3);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const containerRef = useRef<HTMLDivElement>(null);

  // Refs for touch gesture
  const touchStartXRef = useRef<number | null>(null);
  const touchEndXRef = useRef<number | null>(null);
  const MIN_SWIPE_DISTANCE = 50; // Minimum distance for a swipe

  // Initialize currentSlide after visibleSlides is set
  useEffect(() => {
    setCurrentSlide(visibleSlides);
  }, [visibleSlides]);

  // Initialize currentSlide based on the current URL
  useEffect(() => {
    if (posts.length > 0 && visibleSlides > 0) { 
        const slug = location.pathname.split('/blog/')[1];
        if (slug) {
            const index = posts.findIndex(post => post.slug === slug);
            if (index !== -1) {
                setCurrentSlide(index + visibleSlides);
            }
        } else {
            // setCurrentSlide(visibleSlides); // Already handled by the useEffect above
        }
    }
  }, [location.pathname, posts, visibleSlides]);

  useEffect(() => {
    const updateDimensions = () => {
      const width = window.innerWidth;
      setIsMobile(width < 768);
      
      let newVisibleSlides;
      if (width >= 1536) {
        newVisibleSlides = 3;
      } else if (width >= 1024) {
        newVisibleSlides = 3;
      } else if (width >= 768) {
        newVisibleSlides = 2;
      } else {
        newVisibleSlides = 1;
      }
      // Only update if visibleSlides actually changes, to prevent unnecessary rerenders/effects
      setVisibleSlides(prevVisibleSlides => {
        if (prevVisibleSlides !== newVisibleSlides) {
            return newVisibleSlides;
        }
        return prevVisibleSlides;
      });
    };

    updateDimensions();
    window.addEventListener('resize', updateDimensions);
    return () => window.removeEventListener('resize', updateDimensions);
  }, []);

  const allSlides = posts.length > 0 && visibleSlides > 0 ? [
    ...posts.slice(-visibleSlides),
    ...posts,
    ...posts.slice(0, visibleSlides)
  ] : [];

  const handlePrevious = () => {
    if (isTransitioning || allSlides.length === 0) return;
    setIsTransitioning(true);
    setCurrentSlide(prev => prev - 1);
  };

  const handleNext = () => {
    if (isTransitioning || allSlides.length === 0) return;
    setIsTransitioning(true);
    setCurrentSlide(prev => prev + 1);
  };

  const handleTransitionEnd = () => {
    setIsTransitioning(false);
    if (posts.length === 0 || visibleSlides === 0 || allSlides.length === 0) return;

    if (currentSlide < visibleSlides) {
      setCurrentSlide(currentSlide + posts.length);
    } else if (currentSlide >= posts.length + visibleSlides) {
      setCurrentSlide(currentSlide - posts.length);
    }
  };

  const getCurrentIndex = () => {
    if (posts.length === 0 || visibleSlides === 0 || allSlides.length === 0) return 0;
    let actualIndex = currentSlide - visibleSlides;
    actualIndex = actualIndex % posts.length;
    if (actualIndex < 0) {
        actualIndex += posts.length;
    }
    return actualIndex;
  };

  const handlePaginationClick = (index: number) => {
    if (!isTransitioning && posts.length > 0 && visibleSlides > 0) {
      setIsTransitioning(true);
      setCurrentSlide(index + visibleSlides);
    }
  };

  const handleBlogPostClick = (slug: string) => {
    if (touchEndXRef.current !== null && 
        Math.abs((touchEndXRef.current ?? 0) - (touchStartXRef.current ?? 0)) > MIN_SWIPE_DISTANCE / 2) {
        touchStartXRef.current = null;
        touchEndXRef.current = null;
        return; 
    }
    touchStartXRef.current = null;
    touchEndXRef.current = null;

    const params = new URLSearchParams(location.search);
    const langParam = params.get('lang');
    const blogPath = `/blog/${slug}${langParam ? `?lang=${langParam}` : ''}`;
    navigate(blogPath);
  };

  const getSlideWidth = () => {
    // This function must align with the number of slides made visible by Tailwind classes.
    // If Tailwind shows 1 slide (w-full), this should be 100.
    // If Tailwind shows 2 slides (md:w-1/2), this should be 50.
    // If Tailwind shows 3 slides (lg:w-1/3), this should be 33.333.
    // The `visibleSlides` state correctly reflects this based on `updateDimensions`.
    if (visibleSlides === 0) return 100; // Fallback, should not happen
    return 100 / visibleSlides;
  };
  
  const handleTouchStart = (e: React.TouchEvent) => {
    if (isTransitioning || allSlides.length === 0) return;
    touchStartXRef.current = e.touches[0].clientX;
    touchEndXRef.current = e.touches[0].clientX; 
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (isTransitioning || touchStartXRef.current === null || allSlides.length === 0) return;
    touchEndXRef.current = e.touches[0].clientX;
  };

  const handleTouchEnd = () => {
    if (isTransitioning || touchStartXRef.current === null || touchEndXRef.current === null || allSlides.length === 0) {
      touchStartXRef.current = null; 
      touchEndXRef.current = null;
      return;
    }

    const distance = touchEndXRef.current - touchStartXRef.current;

    if (Math.abs(distance) > MIN_SWIPE_DISTANCE) {
      if (distance > 0) { 
        handlePrevious();
      } else { 
        handleNext();
      }
    }
    // Always reset refs after touch end processing is complete
    touchStartXRef.current = null;
    touchEndXRef.current = null;
  };

  const translateXPercent = currentSlide * getSlideWidth();

  if (posts.length === 0) {
    return <div className="text-center p-8">No blog posts to display.</div>;
  }

  // Determine slide item class based on visibleSlides for explicit width control matching translateX
  let slideItemStyle;
  if (visibleSlides === 1) {
    slideItemStyle = { flex: '0 0 100%' };
  } else if (visibleSlides === 2) {
    slideItemStyle = { flex: '0 0 50%' };
  } else { // Default to 3 or more slides
    slideItemStyle = { flex: '0 0 33.333333%' };
  }

  return (
    <div className="relative" ref={containerRef}>
      <div 
        className="overflow-hidden"
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        <div 
          className={`flex ${isTransitioning ? 'transition-transform duration-500 ease-in-out' : ''}`}
          style={{ transform: `translateX(-${translateXPercent}%)` }}
          onTransitionEnd={handleTransitionEnd}
        >
          {allSlides.map((post, index) => (
            <div 
              key={`${post.slug}-${index}`} 
              // Explicitly set flex-basis to align with getSlideWidth for translateX
              style={slideItemStyle} 
              className="px-2 md:px-4 flex-shrink-0" // Keep padding and flex-shrink
            >
              <article 
                className="bg-white rounded-lg shadow-sm hover:shadow-md transition-all overflow-hidden h-full cursor-pointer flex flex-col"
                onClick={() => handleBlogPostClick(post.slug)}
              >
                <div className="relative h-48">
                  {post?.image && (
                    <>
                      <img 
                        src={post.image} 
                        alt={post.title || ''}
                        className="w-full h-full object-cover"
                      />
                      {post.aiGeneratedImage && (
                        <div className="absolute bottom-1 right-2 text-white/80 text-[8px] md:text-[10px] pointer-events-none z-10 drop-shadow-md">
                          AI generated image
                        </div>
                      )}
                    </>
                  )}
                </div>
                <div className="p-6 flex flex-col flex-grow">
                  <h3 className="text-xl font-semibold mb-3 line-clamp-3 min-h-[4.5rem]">{post.title}</h3>
                  <p className="text-gray-600 mb-4 line-clamp-4 min-h-[4.5rem]">{post.excerpt}</p>
                  <div className="mt-auto pt-4 flex items-center justify-between">
                    <time className="text-sm text-gray-500">{formatDate(post.date)}</time>
                    <button 
                      onClick={(e) => {
                        e.stopPropagation();
                        handleBlogPostClick(post.slug);
                      }}
                      className="text-bamboo-600 hover:text-bamboo-700 flex items-center gap-1"
                    >
                      {translations.buttons.readMore} <ChevronRight size={16} />
                    </button>
                  </div>
                </div>
              </article>
            </div>
          ))}
        </div>
      </div>
      
      <button
        onClick={handlePrevious}
        className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-4 p-2 rounded-full bg-white shadow-lg hover:bg-bamboo-50 transition-colors"
        disabled={isTransitioning || allSlides.length === 0}
      >
        <ChevronLeft className="w-6 h-6 text-bamboo-600" />
      </button>

      <button
        onClick={handleNext}
        className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-4 p-2 rounded-full bg-white shadow-lg hover:bg-bamboo-50 transition-colors"
        disabled={isTransitioning || allSlides.length === 0}
      >
        <ChevronRight className="w-6 h-6 text-bamboo-600" />
      </button>
      
      <div className="flex justify-center gap-2 mt-8">
        {posts.map((_, index) => (
          <button
            key={index}
            className={`w-2 h-2 rounded-full transition-colors ${
              getCurrentIndex() === index ? 'bg-bamboo-600' : 'bg-bamboo-200 hover:bg-bamboo-300'
            }`}
            onClick={() => handlePaginationClick(index)}
            disabled={allSlides.length === 0}
          />
        ))}
      </div>
    </div>
  );
}
